import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'

const MAX_HEIGHT_PX = 240

export type SelectDropdownPosition = {
  top?: number
  bottom?: number
  left: number
  width: number
  maxHeight: number
}

export function useSelectDropdownPosition(open: boolean, anchorRef: RefObject<HTMLElement | null>) {
  const [position, setPosition] = useState<SelectDropdownPosition | null>(null)

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPosition(null)
      return
    }

    const update = () => {
      const anchor = anchorRef.current
      if (!anchor) return

      const rect = anchor.getBoundingClientRect()
      const gap = 4
      const spaceBelow = window.innerHeight - rect.bottom - gap
      const spaceAbove = rect.top - gap
      const openUpward = spaceBelow < Math.min(MAX_HEIGHT_PX, 120) && spaceAbove > spaceBelow

      if (openUpward) {
        setPosition({
          bottom: window.innerHeight - rect.top + gap,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.min(MAX_HEIGHT_PX, spaceAbove),
        })
      } else {
        setPosition({
          top: rect.bottom + gap,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.min(MAX_HEIGHT_PX, spaceBelow),
        })
      }
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, anchorRef])

  return position
}
